import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext"; 
import { db } from "../firebaseConfig";
import { doc, updateDoc, deleteDoc } from "firebase/firestore"; 
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function CardRequests() {
  const { cardRequests, loading, isAdmin, isAccount } = useWebData();

  // 🔥 Filter States
  const [viewMode, setViewMode] = useState('Month'); 
  const [selectedDate, setSelectedDate] = useState(new Date()); 
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); 
  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);

  // States
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);

  // ==========================================
  // 1. UNIVERSAL FILTER LOGIC
  // ==========================================
  useEffect(() => {
    if (!cardRequests) return;

    let result = [...cardRequests];

    // Search Logic
    if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        result = result.filter(i => {
            const dateStr = i.createdAt ? new Date(i.createdAt).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric'
            }).toLowerCase() : "";
            const row = `${i.reqId} ${i.userName} ${i.courierName} ${i.trackingNo} ${i.status} ${i.receivedBy}`.toLowerCase();
            return row.includes(lower) || dateStr.includes(lower);
        });
    }

    // Date Logic
    if (viewMode !== 'All') {
        const targetYear = viewMode === 'Year' ? parseInt(selectedYear) : selectedDate.getFullYear();
        const targetMonth = selectedDate.getMonth(); 

        result = result.filter(item => {
            const rawDate = item.createdAt || ""; 
            if (!rawDate) return false;
            let y = 0; let m = -1;
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) { y = d.getFullYear(); m = d.getMonth(); } 
            else {
                const parts = rawDate.split(/[-/]/);
                if (parts.length === 3) {
                    if (parts[2].length === 4) { y = parseInt(parts[2]); m = parseInt(parts[1]) - 1; } 
                    else if (parts[0].length === 4) { y = parseInt(parts[0]); m = parseInt(parts[1]) - 1; }
                }
            }
            if (viewMode === 'Year') return y === targetYear;
            if (viewMode === 'Month') return y === targetYear && m === targetMonth;
            return true;
        });
    }

    result.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    setFiltered(result);
  }, [cardRequests, searchTerm, viewMode, selectedDate, selectedYear]); 

  // ==========================================
  // 2. EXCEL DOWNLOAD (All Details)
  // ==========================================
  const downloadExcel = () => {
    const dataToExport = filtered.map(i => ({
        Req_Date: formatDate(i.createdAt),
        Req_ID: i.reqId, 
        Employee: i.userName, 
        Items: i.items?.map(it => `${it.type} (${it.quantity})`).join(", ") || "-",
        Address: i.shippingAddress,
        
        // Dispatch Info
        Courier_Name: i.courierName || "-",
        Tracking_No: i.trackingNo || "-",
        Dispatch_Date: formatDate(i.dispatchDate), // 🔥 Included
        
        Status: i.status,
        
        // Receive Info
        Received_By: i.receivedBy || "-",
        Received_Date: formatDate(i.receiveDate) // 🔥 Included
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CardRequests");
    XLSX.writeFile(wb, `CardRequests_${viewMode}.xlsx`);
  };

  // --- ACTIONS ---
  
  const updateStatus = async (row, status) => {
      if(window.confirm(`Update status to ${status}?`)) {
          try {
              const updatePayload = { status };
              const now = new Date().toISOString();

              if(status === 'Sent') {
                  updatePayload.dispatchDate = now;
              }
              if(status === 'Delivered') {
                  updatePayload.receiveDate = now;
                  updatePayload.receivedBy = row.userName;
                  // Agar Dispatch Date pehle nahi thi, to aaj ki daal do (Fallback)
                  if(!row.dispatchDate) updatePayload.dispatchDate = now;
              }

              await updateDoc(doc(db, "card_requests", row.id), updatePayload);
              
              if(viewData && viewData.id === row.id) {
                  setViewData({...viewData, ...updatePayload});
              }
          } catch(e) { alert("Error updating status"); }
      }
  };

  const handleSaveEdit = async () => {
      if(!editData) return;
      try {
          await updateDoc(doc(db, "card_requests", editData.id), {
              shippingAddress: editData.shippingAddress,
              courierName: editData.courierName, 
              trackingNo: editData.trackingNo, 
              dispatchDate: editData.dispatchDate, 
              receivedBy: editData.receivedBy,     
              receiveDate: editData.receiveDate,   
              status: editData.status
          });
          alert("Request Updated ✅");
          setEditData(null);
      } catch (e) { alert("Error updating"); }
  };

  const handleDelete = async (id) => {
      if(window.confirm("Delete this request?")) await deleteDoc(doc(db, "card_requests", id));
  };

  // Helpers
  const handleMonthChange = (dir) => {
      const d = new Date(selectedDate);
      d.setMonth(d.getMonth() + dir);
      setSelectedDate(d);
  };

  const getYearOptions = () => {
      const currentY = new Date().getFullYear();
      const years = [];
      for(let y = 2024; y <= currentY + 5; y++) years.push(y);
      return years;
  };

  const formatDate = (iso) => {
      if(!iso) return "-";
      const date = new Date(iso);
      return isNaN(date.getTime()) ? iso : date.toLocaleDateString('en-GB'); // DD/MM/YYYY
  };

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", backgroundColor:"#f8f9fa", minHeight:"100vh"}}>
      
      {/* HEADER */}
      <div style={headerCard}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px"}}>
            <h2 style={{margin:0, color:"#8e44ad", display:"flex", alignItems:"center"}}>
                📇 Card Requests <span style={{fontSize:"14px", color:"#777", marginLeft:"10px"}}>({filtered.length})</span>
            </h2>
            <button onClick={downloadExcel} style={btnDownload}>📥 Download Excel</button>
        </div>

        <div style={{display:"flex", gap:"10px", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between"}}>
            <div style={{display:"flex", gap:"5px", backgroundColor:"#eee", padding:"4px", borderRadius:"6px"}}>
                {['Month', 'Year', 'All'].map(mode => (
                    <button key={mode} onClick={()=>setViewMode(mode)} 
                        style={{...toggleBtn, backgroundColor: viewMode===mode ? "white" : "transparent", color: viewMode===mode ? "#333" : "#777", boxShadow: viewMode===mode ? "0 2px 5px rgba(0,0,0,0.1)" : "none"}}>
                        {mode}
                    </button>
                ))}
            </div>

            {viewMode === 'Month' && (
                <div style={{display:"flex", alignItems:"center", gap:"10px", backgroundColor:"white", padding:"5px 15px", borderRadius:"6px", border:"1px solid #ddd"}}>
                    <button onClick={()=>handleMonthChange(-1)} style={navBtn}>◀</button>
                    <span style={{fontWeight:"bold", color:"#333", minWidth:"120px", textAlign:"center"}}>
                        {selectedDate.toLocaleDateString('en-US', {month:'long', year:'numeric'})}
                    </span>
                    <button onClick={()=>handleMonthChange(1)} style={navBtn}>▶</button>
                </div>
            )}

            {viewMode === 'Year' && (
                <select 
                    value={selectedYear} 
                    onChange={(e) => setSelectedYear(e.target.value)}
                    style={{padding:"8px", borderRadius:"6px", border:"1px solid #ddd", fontWeight:"bold", fontSize:"14px", cursor:"pointer"}}
                >
                    {getYearOptions().map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
            )}

            <input type="text" placeholder="🔍 Search Tracking, ID, Name..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={searchInput} />
        </div>
      </div>

      {loading ? <Loader /> : (
        <div style={{overflowX:"auto", backgroundColor:"white", borderRadius:"8px", boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
          <table style={tableStyle}>
            <thead>
                <tr style={{backgroundColor:"#8e44ad", color:"white", textAlign:"left"}}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Req ID</th>
                    <th style={thStyle}>Employee</th>
                    <th style={thStyle}>Address</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Actions</th>
                </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                  <tr><td colSpan="6" style={{padding:"20px", textAlign:"center", color:"#999"}}>No requests found.</td></tr>
              ) : filtered.map(row => (
                <tr key={row.id} style={{borderBottom:"1px solid #eee", height:"45px"}}>
                    <td style={tdStyle}>{formatDate(row.createdAt)}</td>
                    <td style={{...tdStyle, fontWeight:"bold", color:"#2c3e50"}}>{row.reqId}</td>
                    <td style={tdStyle}>{row.userName}</td>
                    <td style={{...tdStyle, maxWidth:"200px", fontSize:"11px", color:"#555"}} title={row.shippingAddress}>
                        {row.shippingAddress ? row.shippingAddress.substring(0, 30) + "..." : "-"}
                    </td>
                    <td style={tdStyle}>
                        <span style={{
                            padding:"4px 10px", borderRadius:"12px", fontSize:"11px", fontWeight:"bold",
                            backgroundColor: row.status==='Delivered'?'#e8f5e9':(row.status==='Sent'?'#e3f2fd':'#fff3e0'),
                            color: row.status==='Delivered'?'green':(row.status==='Sent'?'blue':'orange')
                        }}>
                            {row.status}
                        </span>
                    </td>
                    <td style={tdStyle}>
                        <div style={{display:"flex", gap:"3px"}}>
                            {(isAdmin || isAccount) && (
                                <>
                                    {row.status === 'Pending' && <button onClick={()=>updateStatus(row,'Sent')} style={btnSent} title="Mark Sent">📦 Sent</button>}
                                    {row.status === 'Sent' && <button onClick={()=>updateStatus(row,'Delivered')} style={btnDeliver} title="Mark Delivered">✅ Done</button>}
                                </>
                            )}
                            <button onClick={() => setViewData(row)} style={btnIconEye} title="View">👁️</button>
                            {(isAdmin || isAccount) && (
                                <>
                                    <button onClick={() => setEditData(row)} style={btnIconEdit} title="Edit">✏️</button>
                                    <button onClick={() => handleDelete(row.id)} style={btnIconDelete} title="Delete">🗑️</button>
                                </>
                            )}
                        </div>
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 🔥 VIEW MODAL (NOW SHOWS ALL 3 SECTIONS) */}
      {viewData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px", borderBottom:"1px solid #eee", paddingBottom:"10px"}}>
                <h3 style={{margin:0, color:"#8e44ad"}}>📇 Request Details</h3>
                <button onClick={() => setViewData(null)} style={{background:"none", border:"none", fontSize:"18px", cursor:"pointer"}}>✖</button>
            </div>
            
            <div style={{fontSize:"13px", lineHeight:"1.6"}}>
                
                {/* 1. Request Info (Always Show) */}
                <div style={{marginBottom:"10px", paddingBottom:"10px", borderBottom:"1px dashed #eee"}}>
                    <h4 style={{margin:"0 0 5px 0", color:"#555"}}>📝 Request Info</h4>
                    <p style={{margin:"2px 0"}}><strong>🔢 Req ID:</strong> {viewData.reqId}</p>
                    <p style={{margin:"2px 0"}}><strong>👤 Employee:</strong> {viewData.userName}</p>
                    <p style={{margin:"2px 0"}}><strong>📅 Req Date:</strong> {formatDate(viewData.createdAt)}</p>
                    <p style={{margin:"2px 0"}}><strong>🚚 Address:</strong> {viewData.shippingAddress}</p>
                </div>

                {/* 2. Dispatch Info (Show if Sent or Delivered) */}
                {(viewData.status === 'Sent' || viewData.status === 'Delivered') && (
                    <div style={{background:"#f3e5f5", padding:"8px", borderRadius:"6px", marginBottom:"10px"}}>
                        <h4 style={{margin:"0 0 5px 0", color:"#8e44ad", fontSize:"12px"}}>🚀 Dispatch Details</h4>
                        <p style={{margin:0}}><strong>🚚 Courier:</strong> {viewData.courierName || "-"}</p>
                        <p style={{margin:0}}><strong>📦 Tracking:</strong> {viewData.trackingNo || "-"}</p>
                        <p style={{margin:0}}><strong>📅 Dispatched:</strong> {formatDate(viewData.dispatchDate)}</p>
                    </div>
                )}

                {/* 3. Delivery Info (Show if Delivered) */}
                {viewData.status === 'Delivered' && (
                    <div style={{background:"#e8f5e9", padding:"8px", borderRadius:"6px", marginBottom:"10px"}}>
                        <h4 style={{margin:"0 0 5px 0", color:"green", fontSize:"12px"}}>✅ Receiving Details</h4>
                        <p style={{margin:0}}><strong>👤 Received By:</strong> {viewData.receivedBy || "-"}</p>
                        <p style={{margin:0}}><strong>📅 Received On:</strong> {formatDate(viewData.receiveDate)}</p>
                    </div>
                )}

                <strong>📝 Items:</strong>
                <ul style={{marginTop:"5px", paddingLeft:"20px", background:"#f9f9f9", borderRadius:"5px", padding:"10px"}}>
                    {viewData.items?.map((item, idx) => (
                        <li key={idx} style={{marginBottom:"5px"}}>{item.type} - <strong>{item.quantity}</strong></li>
                    ))}
                </ul>
            </div>
            <button onClick={() => setViewData(null)} style={btnClose}>Close</button>
          </div>
        </div>
      )}

      {/* 🔥 EDIT MODAL (UPDATED) */}
      {editData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{color:"#f39c12", marginTop:0}}>✏️ Edit Request</h3>
            
            <div style={{maxHeight:"400px", overflowY:"auto", paddingRight:"5px"}}>
                <label style={labelStyle}>Shipping Address</label>
                <input type="text" value={editData.shippingAddress} onChange={(e)=>setEditData({...editData, shippingAddress:e.target.value})} style={modalInput}/>
                
                <div style={{display:"flex", gap:"10px"}}>
                    <div style={{flex:1}}>
                        <label style={labelStyle}>Courier Name</label>
                        <input type="text" value={editData.courierName || ''} onChange={(e)=>setEditData({...editData, courierName:e.target.value})} style={modalInput} placeholder="Courier"/>
                    </div>
                    <div style={{flex:1}}>
                        <label style={labelStyle}>Tracking No</label>
                        <input type="text" value={editData.trackingNo || ''} onChange={(e)=>setEditData({...editData, trackingNo:e.target.value})} style={modalInput}/>
                    </div>
                </div>

                {/* Manual Date Overrides */}
                <div style={{display:"flex", gap:"10px"}}>
                    <div style={{flex:1}}>
                        <label style={labelStyle}>Dispatch Date</label>
                        <input type="date" value={editData.dispatchDate ? editData.dispatchDate.split('T')[0] : ''} onChange={(e)=>setEditData({...editData, dispatchDate:e.target.value})} style={modalInput}/>
                    </div>
                    <div style={{flex:1}}>
                        <label style={labelStyle}>Received Date</label>
                        <input type="date" value={editData.receiveDate ? editData.receiveDate.split('T')[0] : ''} onChange={(e)=>setEditData({...editData, receiveDate:e.target.value})} style={modalInput}/>
                    </div>
                </div>

                <label style={labelStyle}>Received By Name</label>
                <input type="text" value={editData.receivedBy || ''} onChange={(e)=>setEditData({...editData, receivedBy:e.target.value})} style={modalInput} placeholder="Receiver Name"/>

                <label style={labelStyle}>Status</label>
                <select value={editData.status} onChange={(e)=>setEditData({...editData, status:e.target.value})} style={modalInput}>
                    <option value="Pending">Pending</option>
                    <option value="Sent">Sent 📦</option>
                    <option value="Delivered">Delivered ✅</option>
                </select>
            </div>

            <div style={{marginTop:"15px", textAlign:"right"}}>
                <button onClick={()=>setEditData(null)} style={btnCancel}>Cancel</button>
                <button onClick={handleSaveEdit} style={btnSave}>Update</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// STYLES
const headerCard={backgroundColor:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", marginBottom:"20px"};
const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px"};
const thStyle={padding:"12px 15px", borderBottom:"2px solid #eee", fontSize:"12px", textTransform:"uppercase", letterSpacing:"0.5px"};
const tdStyle={padding:"10px 15px", verticalAlign:"middle"};
const toggleBtn={border:"none", padding:"6px 12px", borderRadius:"5px", cursor:"pointer", fontSize:"13px", fontWeight:"600", transition:"0.2s"};
const navBtn={background:"none", border:"none", cursor:"pointer", fontSize:"16px", color:"#555"};
const searchInput={padding:"8px 12px", border:"1px solid #ddd", borderRadius:"6px", width:"200px", outline:"none"};
const btnDownload={backgroundColor:"#27ae60", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px"};

const btnSent={backgroundColor:"#3498db", color:"white", border:"none", padding:"4px 8px", borderRadius:"4px", cursor:"pointer", fontSize:"11px", marginRight:"3px", fontWeight:"bold"};
const btnDeliver={backgroundColor:"#27ae60", color:"white", border:"none", padding:"4px 8px", borderRadius:"4px", cursor:"pointer", fontSize:"11px", marginRight:"3px", fontWeight:"bold"};
const btnIconEye={backgroundColor:"#e3f2fd", color:"#1976d2", border:"1px solid #bbdefb", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconEdit={backgroundColor:"#fff3e0", color:"#e67e22", border:"1px solid #ffe0b2", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconDelete={backgroundColor:"#fff", color:"#777", border:"1px solid #ddd", padding:"5px 8px", borderRadius:"4px", cursor:"pointer"};

const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"25px", borderRadius:"12px", width:"400px", boxShadow:"0 10px 30px rgba(0,0,0,0.2)", position:"relative", maxHeight:"90vh", overflowY:"auto"};
const modalInput={width:"100%", padding:"8px", margin:"5px 0", borderRadius:"4px", border:"1px solid #ccc", boxSizing:"border-box", fontSize:"13px"};
const labelStyle={fontSize:"11px", fontWeight:"bold", color:"#555"};
const btnSave={backgroundColor:"#8e44ad", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer", marginLeft:"5px"};
const btnCancel={backgroundColor:"#95a5a6", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer"};
const btnClose={marginTop:"15px", backgroundColor:"#333", color:"white", border:"none", padding:"8px", borderRadius:"4px", width:"100%", cursor:"pointer"};

export default CardRequests;